/**
 * Buying things in.
 *
 * Three lists that are really one sequence: an order goes to a supplier, goods
 * arrive against it, and the supplier is who both belong to. They are tabs
 * rather than three navigation entries because nobody thinks of them
 * separately — they think about "that delivery".
 *
 * Deliveries can be taken in here. That was held back on the belief that a
 * branch raising its own paperwork would be paperwork the cloud never saw —
 * which was wrong: orders and deliveries travel up, unlike the menu, because
 * the branch is where they happen and the branch is what knows about them.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPKR, parseServerTime } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Tabs } from '../../components/ui/Tabs.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ReceivePanel } from './ReceivePanel.jsx';
import { RaiseOrderPanel } from './RaiseOrderPanel.jsx';
import { SupplierForm } from './SupplierForm.jsx';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { orderTime } from '../orders/orderStatus.js';

/** The states a purchase order moves through, in words. */
const PO_STATUS = {
  draft: { label: 'Draft', tone: 'text-ink-3' },
  pending: { label: 'Awaiting approval', tone: 'text-warn' },
  approved: { label: 'Approved', tone: 'text-ink-2' },
  sent: { label: 'Sent to supplier', tone: 'text-ink-2' },
  partial: { label: 'Part delivered', tone: 'text-warn' },
  received: { label: 'Delivered', tone: 'text-good' },
  cancelled: { label: 'Cancelled', tone: 'text-bad' },
};

function day(value) {
  if (!value) return '—';

  const date = parseServerTime(value);
  if (!date) return String(value);

  return date.toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: '2-digit' });
}

export default function PurchasingPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('orders');
  const [receiving, setReceiving] = useState(null);
  const [raising, setRaising] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const orders = useQuery({
    queryKey: ['purchasing', 'orders'],
    queryFn: () => api.get('/purchasing/orders'),
  });

  const grns = useQuery({
    queryKey: ['purchasing', 'grn'],
    queryFn: () => api.get('/purchasing/grn'),
  });

  const suppliers = useQuery({
    queryKey: ['purchasing', 'suppliers'],
    queryFn: () => api.get('/purchasing/suppliers'),
    staleTime: 5 * 60_000,
  });

  const lists = {
    orders: orders.data?.orders ?? [],
    grns: grns.data?.grns ?? [],
    suppliers: suppliers.data?.suppliers ?? [],
  };

  return (
    <div className="mx-auto max-w-5xl p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-semibold text-xl text-ink">Purchasing</h1>
        {!raising && !receiving && tab !== 'suppliers' && (
          <Button onClick={() => setRaising(true)}>Raise an order</Button>
        )}
        {tab === 'suppliers' && !editingSupplier && (
          <Button onClick={() => setEditingSupplier({})}>Add a supplier</Button>
        )}
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'orders', label: 'Orders', count: lists.orders.length },
          { id: 'grns', label: 'Deliveries', count: lists.grns.length },
          { id: 'suppliers', label: 'Suppliers', count: lists.suppliers.length },
        ]}
      />

      <div className="mt-4">
        {raising && (
          <div className="mb-4">
            <RaiseOrderPanel
              onClose={() => setRaising(false)}
              onRaised={() => {
                queryClient.invalidateQueries({ queryKey: ['purchasing'] });
                setRaising(false);
                setTab('orders');
              }}
            />
          </div>
        )}

        {receiving && (
          <div className="mb-4">
            <ReceivePanel
              poId={receiving}
              onClose={() => setReceiving(null)}
              onReceived={() => {
                queryClient.invalidateQueries({ queryKey: ['purchasing'] });
                queryClient.invalidateQueries({ queryKey: ['inventory'] });
                setReceiving(null);
                setTab('grns');
              }}
            />
          </div>
        )}

        {tab === 'orders' && (
          <DataTable
            rows={lists.orders}
            loading={orders.isLoading}
            error={orders.isError}
            searchable
            searchKeys={['po_number', 'supplier_name']}
            empty="No purchase orders yet."
            columns={[
              { key: 'po_number', label: 'Order', tone: () => 'text-ink' },
              { key: 'supplier_name', label: 'Supplier' },
              { key: 'order_date', label: 'Raised', render: (row) => day(row.order_date) },
              {
                key: 'expected_delivery',
                label: 'Expected',
                render: (row) => day(row.expected_delivery),
              },
              {
                key: 'status',
                label: 'Status',
                render: (row) => PO_STATUS[row.status]?.label ?? row.status,
                tone: (row) => PO_STATUS[row.status]?.tone ?? 'text-ink-2',
              },
              {
                key: 'total_amount',
                label: 'Total',
                align: 'right',
                render: (row) => formatPKR(row.total_amount),
                tone: () => 'text-ink',
              },
              {
                key: 'receive',
                label: '',
                align: 'right',
                // Only where something is still owed. An order already fully
                // in is not a delivery waiting at the door.
                render: (row) =>
                  ['received', 'cancelled'].includes(row.status) ? null : (
                    <button
                      type="button"
                      onClick={() => setReceiving(row.id)}
                      className="text-sm text-ink-2 underline hover:text-ink"
                    >
                      Take it in
                    </button>
                  ),
              },
            ]}
          />
        )}

        {tab === 'grns' && (
          <DataTable
            rows={lists.grns}
            loading={grns.isLoading}
            error={grns.isError}
            searchable
            searchKeys={['grn_number', 'po_number', 'supplier_name']}
            empty="Nothing received yet."
            columns={[
              { key: 'grn_number', label: 'Delivery', tone: () => 'text-ink' },
              { key: 'po_number', label: 'Against order' },
              { key: 'supplier_name', label: 'Supplier' },
              {
                key: 'created_at',
                label: 'Received',
                render: (row) => `${day(row.created_at)} ${orderTime(row.created_at)}`,
              },
            ]}
          />
        )}

        {tab === 'suppliers' && editingSupplier && (
          <SupplierForm
            initial={editingSupplier}
            onClose={() => setEditingSupplier(null)}
            onSaved={() => {
              queryClient.invalidateQueries({ queryKey: ['purchasing'] });
              setEditingSupplier(null);
            }}
          />
        )}

        {tab === 'suppliers' && (
          <DataTable
            rows={lists.suppliers}
            loading={suppliers.isLoading}
            error={suppliers.isError}
            searchable
            searchKeys={['name', 'contact_person', 'phone']}
            empty="No suppliers set up yet."
            columns={[
              { key: 'name', label: 'Supplier', tone: () => 'text-ink' },
              { key: 'contact_person', label: 'Contact' },
              { key: 'phone', label: 'Phone' },
              {
                key: 'payment_terms_days',
                label: 'Terms',
                // A bare 30 in a column called Terms means nothing.
                render: (row) =>
                  row.payment_terms_days > 0 ? `${row.payment_terms_days} days` : 'On delivery',
              },
              { key: 'total_orders', label: 'Orders', align: 'right' },
              {
                key: 'edit',
                label: '',
                align: 'right',
                render: (row) => (
                  <button
                    type="button"
                    onClick={() => setEditingSupplier(row)}
                    className="text-sm text-ink-2 underline hover:text-ink"
                  >
                    Change
                  </button>
                ),
              },
              {
                key: 'total_value',
                label: 'Spent',
                align: 'right',
                render: (row) => formatPKR(row.total_value),
                tone: () => 'text-ink',
              },
              {
                key: 'is_active',
                label: 'Active',
                render: (row) => (row.is_active ? 'Yes' : 'No'),
                tone: (row) => (row.is_active ? 'text-ink-2' : 'text-warn'),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}
